import keys from '@debbie/common/dist/cache/keys.js'
import { eq } from '@debbie/common/dist/query/index.js'
import { useMutation } from '@debbie/common/dist/util/hooks.js'
import { debounce } from '@debbie/common/dist/util/index.js'
import * as viewsApi from '@debbie/cortex/dist/api/views.js'
import { h } from '@debbie/cortex/dist/index.js'
import q from '@debbie/cortex/dist/query.js'
import { useQuery } from '@debbie/cortex/dist/query/index.js'
import { RootState } from '@debbie/cortex/dist/reducer.js'
import { viewsEntity } from '@debbie/cortex/dist/views/index.js'
import Disclaimer from '@debbie/iris/dist/components/Disclaimer.js'
import { Check } from '@debbie/iris/dist/components/Icons/index.js'
import {
  Content,
  Errors,
  Loading,
  PageBase,
  Panel,
  PlaceholderLoader,
  TopNav,
} from '@debbie/iris/dist/components/index.js'
import THEME from '@debbie/iris/dist/theme.js'
import { errorNotification } from '@debbie/iris/dist/utils/errors.js'
import { useAtom } from 'jotai'
import React, { useEffect, useRef, useState } from 'react'
import { useSelector } from 'react-redux'
import { useParams } from 'react-router'
import { styled } from 'styled-components'

import { viewPatches } from './state.js'
import { ViewPage } from './ViewPage.js'

type Props = {}

export const IdViewPage = (props: Props) => {
  const { id } = useParams<{ id: string }>()
  const lang = useSelector((s: RootState) => s.lang)
  const [localPatches, setLocalPatches] = useAtom(viewPatches)

  const dataLoader = useQuery('views')({
    base: 'views',
    properties: {
      views: {
        fields: ['id', 'name', 'options', 'visibility'],
      },
    },
    filters: eq('views.id', id),
    orderBy: 'createdAt',
    orderByDirection: 'DESC',
    limit: 1,
    offset: 0,
  })

  const [name, setName] = useState('')
  const [loaded, setLoaded] = useState(false)

  const inputRef = useRef<HTMLInputElement>(null)

  useEffect(() => {
    if (h.hasData(dataLoader) && dataLoader.data.items.length != 0 && !loaded) {
      const name = dataLoader.data.items[0].views.name
      setName(name)
      setLoaded(true)
      if (inputRef.current != null && name === '') {
        inputRef.current.focus()
      }
    }
  }, [dataLoader.loadingState])

  const resetLocalPatches = () => {
    setLocalPatches((patches) => {
      return { ...patches, [id]: undefined }
    })
  }

  const updatingName = useMutation(viewsApi.updateById, {
    onSuccess: (res, id) => {
      q.invalidate(keys.write.modify(viewsEntity, id))
    },
    onError: (err) => {
      errorNotification(lang, err, 'update', lang.errorEntities.view)
    },
  })

  const updatingOptions = useMutation(viewsApi.updateById, {
    onSuccess: (res, id) => {
      q.invalidate(keys.write.modify(viewsEntity, id))
      // TODO: check if patches have been applied since request was sent
      resetLocalPatches()
    },
    onError: (err) => {
      errorNotification(lang, err, 'update', lang.errorEntities.view)
    },
  })

  if (h.hasData(dataLoader)) {
    if (dataLoader.data.items.length === 0) {
      return (
        <>
          <TopNav />
          <Content>
            <PageBase>
              <Panel>
                <Errors errors={[lang.errors.notFound]} />
              </Panel>
            </PageBase>
          </Content>
        </>
      )
    }

    const view = dataLoader.data.items[0].views

    return (
      <>
        <TopNav />
        <Content>
          <PageBase>
            <Disclaimer>
              <p>{lang.views.betaDisclaimer}</p>
            </Disclaimer>
            <Panel
              header={
                <Header>
                  <HeaderInputWrapper>
                    <HeaderInput
                      ref={inputRef}
                      placeholder={lang.views.name}
                      value={name}
                      onChange={(e) => {
                        const value = e.currentTarget.value
                        debounce(
                          `updateViews-name-${view.id}`,
                          () => {
                            updatingName.mutate(view.id, {
                              name: value,
                            })
                          },
                          500,
                        )
                        setName(value)
                      }}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter' && inputRef.current) {
                          inputRef.current.blur()
                        }
                      }}
                    ></HeaderInput>
                    {updatingName.isLoading || updatingOptions.isLoading ? (
                      <Loading width="1em"></Loading>
                    ) : updatingName.recentMutation ||
                      updatingOptions.recentMutation ? (
                      <Check></Check>
                    ) : null}
                  </HeaderInputWrapper>
                  <div></div>
                </Header>
              }
            >
              <ViewPage
                viewDescription={localPatches[view.id] ?? view.options}
                updating={updatingOptions.isLoading}
                onSave={(desc) => {
                  updatingOptions.mutate(view.id, { options: desc })
                }}
                localChanges={localPatches[view.id] != null}
                resetLocal={resetLocalPatches}
                onAutoSave={(desc) => {
                  setLocalPatches((patches) => {
                    return { ...patches, [view.id]: desc }
                  })
                }}
              />
            </Panel>
          </PageBase>
        </Content>
      </>
    )
  }

  if (h.isLoading(dataLoader)) {
    return (
      <>
        <TopNav />
        <Content>
          <PageBase>
            <PlaceholderLoader />
          </PageBase>
        </Content>
      </>
    )
  }

  if (h.isError(dataLoader)) {
    return (
      <>
        <TopNav />
        <Content>
          <PageBase>
            {dataLoader.error instanceof Error ? (
              <Errors errors={[dataLoader.error.message]} />
            ) : (
              <Errors errors={[lang.misc.errorOccured]} />
            )}
          </PageBase>
        </Content>
      </>
    )
  }

  return <></>
}

const Header = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
`

const HeaderInputWrapper = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  width: 100%;
`

const HeaderInput = styled.input`
  width: 100%;
  background: none;
  border: none;
  font-size: 1em;
  outline: none;
  font-weight: bold;
  color: ${THEME.COLOR_PRIMARY};
`
